import React, { useState, useEffect } from 'react';
import {
    Paper,
    Typography,
    Grid,
    TextField,
    Button,
    Box,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    IconButton,
    InputAdornment,
} from '@mui/material';
import {
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useApiHandler } from '../../hooks/useApiHandler';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import customerProfileService from '../../services/customerProfileService';

const CustomerProfile = () => {
    const { showSuccess, showError } = useApiHandler();
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState(null);
    const [changePasswordOpen, setChangePasswordOpen] = useState(false);
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false,
    });
    const [passwordData, setPasswordData] = useState({
        current_password: '',
        new_password: '',
        new_password_confirmation: '',
    });
    const [passwordError, setPasswordError] = useState('');

    useEffect(() => {
        loadProfile();
    }, []);

    const loadProfile = async () => {
        setLoading(true);
        try {
            const response = await customerProfileService.getProfile();
            if (response.success) {
                setProfile(response.data);
            } else {
                showError(response.message || 'Failed to load profile');
            }
        } catch (error) {
            console.error('Error loading profile:', error);
            showError('Failed to load profile');
        } finally {
            setLoading(false);
        }
    };

    const handleChangePasswordOpen = () => {
        setChangePasswordOpen(true);
        setPasswordData({
            current_password: '',
            new_password: '',
            new_password_confirmation: '',
        });
        setPasswordError('');
    };

    const handleChangePasswordClose = () => {
        setChangePasswordOpen(false);
        setPasswordData({
            current_password: '',
            new_password: '',
            new_password_confirmation: '',
        });
        setPasswordError('');
    };

    const handlePasswordChange = (field) => (event) => {
        setPasswordData({
            ...passwordData,
            [field]: event.target.value,
        });
        setPasswordError('');
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords({
            ...showPasswords,
            [field]: !showPasswords[field],
        });
    };

    const handlePasswordSubmit = async () => {
        // Validate passwords
        if (!passwordData.current_password || !passwordData.new_password || !passwordData.new_password_confirmation) {
            setPasswordError('All fields are required');
            return;
        }

        if (passwordData.new_password.length < 6) {
            setPasswordError('New password must be at least 6 characters');
            return;
        }

        if (passwordData.new_password !== passwordData.new_password_confirmation) {
            setPasswordError('New passwords do not match');
            return;
        }

        setPasswordLoading(true);
        try {
            const response = await customerProfileService.changePassword(passwordData);
            if (response.success) {
                showSuccess('Password changed successfully');
                handleChangePasswordClose();
            } else {
                setPasswordError(response.message || 'Failed to change password');
            }
        } catch (error) {
            console.error('Error changing password:', error);
            if (error.response?.data?.message) {
                setPasswordError(error.response.data.message);
            } else if (error.response?.data?.errors) {
                const errors = Object.values(error.response.data.errors).flat();
                setPasswordError(errors.join(', '));
            } else {
                setPasswordError('Failed to change password');
            }
        } finally {
            setPasswordLoading(false);
        }
    };

    if (loading) {
        return (
            <DashboardLayout title="My Profile" userType="customer">
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <CircularProgress />
                </Box>
            </DashboardLayout>
        );
    }

    if (!profile) {
        return (
            <DashboardLayout title="My Profile" userType="customer">
                <Alert severity="error">Failed to load profile data</Alert>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout title="My Profile" userType="customer">
            <Paper sx={{ p: 3 }}>
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h5" gutterBottom>
                            Profile Information
                        </Typography>
                        <Typography color="textSecondary">
                            View your profile details
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        onClick={handleChangePasswordOpen}
                    >
                        Change Password
                    </Button>
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                    {/* Account Information */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                            Account Information
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Customer Code"
                            value={profile.customer_code || ''}
                            disabled
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Username"
                            value={profile.username || ''}
                            disabled
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Member Since"
                            value={profile.member_since || ''}
                            disabled
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Total Shipments"
                            value={profile.total_shipments || 0}
                            disabled
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                    </Grid>

                    {/* Contact Information */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                            Contact Information
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Customer Name"
                            value={profile.customer_name || ''}
                            disabled
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Contact Person"
                            value={profile.contact_name || ''}
                            disabled
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Company Name"
                            value={profile.company_name || ''}
                            disabled
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            value={profile.email || ''}
                            disabled
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Mobile Number"
                            value={profile.mobile || ''}
                            disabled
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                    </Grid>

                    {/* Address Information */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                            Address Information
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Address"
                            multiline
                            rows={2}
                            value={profile.address || ''}
                            disabled
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="City"
                            value={profile.city || ''}
                            disabled
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="State"
                            value={profile.state || ''}
                            disabled
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Pincode"
                            value={profile.pincode || ''}
                            disabled
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                    </Grid>

                    {/* Business Information */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                            Business Information
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="GST Number"
                            value={profile.gst_number || ''}
                            disabled
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Credit Days"
                            value={profile.credit_days || ''}
                            disabled
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                    </Grid>
                    {profile.bankers_details && (
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Bankers Details"
                                multiline
                                rows={2}
                                value={profile.bankers_details || ''}
                                disabled
                                InputProps={{
                                    readOnly: true,
                                }}
                            />
                        </Grid>
                    )}
                    {profile.trade_details && (
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Trade Details"
                                multiline
                                rows={2}
                                value={profile.trade_details || ''}
                                disabled
                                InputProps={{
                                    readOnly: true,
                                }}
                            />
                        </Grid>
                    )}
                </Grid>
            </Paper>

            {/* Change Password Dialog */}
            <Dialog open={changePasswordOpen} onClose={handleChangePasswordClose} maxWidth="sm" fullWidth>
                <DialogTitle>Change Password</DialogTitle>
                <DialogContent>
                    {passwordError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {passwordError}
                        </Alert>
                    )}
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            label="Current Password"
                            type={showPasswords.current ? 'text' : 'password'}
                            value={passwordData.current_password}
                            onChange={handlePasswordChange('current_password')}
                            sx={{ mb: 2 }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => togglePasswordVisibility('current')}
                                            edge="end"
                                        >
                                            {showPasswords.current ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            fullWidth
                            label="New Password"
                            type={showPasswords.new ? 'text' : 'password'}
                            value={passwordData.new_password}
                            onChange={handlePasswordChange('new_password')}
                            sx={{ mb: 2 }}
                            helperText="Minimum 6 characters"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => togglePasswordVisibility('new')}
                                            edge="end"
                                        >
                                            {showPasswords.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Confirm New Password"
                            type={showPasswords.confirm ? 'text' : 'password'}
                            value={passwordData.new_password_confirmation}
                            onChange={handlePasswordChange('new_password_confirmation')}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => togglePasswordVisibility('confirm')}
                                            edge="end"
                                        >
                                            {showPasswords.confirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleChangePasswordClose}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handlePasswordSubmit}
                        disabled={passwordLoading}
                    >
                        {passwordLoading ? <CircularProgress size={24} /> : 'Change Password'}
                    </Button>
                </DialogActions>
            </Dialog>
        </DashboardLayout>
    );
};

export default CustomerProfile;
